import React, { FunctionComponent, ReactNode } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import Translate from 'ui/atoms/translate';
import Button from 'ui/atoms/button';
import LoadingRing from 'ui/atoms/loading-ring';
import { ApiServerError, UnknownServerError } from 'core/api/errors';
import { handleError } from 'ui/helper/error-handling';
import useTranslate from 'ui/hooks/use-translate';
import Hint from 'ui/atoms/hint';

export interface TfaDeviceSetupModalProps {
  /** Primary content. */
  children?: ReactNode;

  /** Additional classes. */
  className?: string;

  loading?: boolean;

  user?: { email: string };

  onResendEmail?: () => void;

  onClose?: () => void;

  error?: ApiServerError | UnknownServerError;
}

const TfaDeviceSetupModal: FunctionComponent<TfaDeviceSetupModalProps> = ({
  className,
  user,
  onResendEmail = () => {},
  onClose = () => {},
  error,
}) => {
  const title = <Translate name="portfolio.tfaSetupModal.title" />;

  const { getRemainingError } = handleError({
    error,
    translate: useTranslate(),
  });

  const remainingError = getRemainingError();

  return (
    <Modal onClose={onClose} className={cx('tfa-device-setup-modal', className)}>
      <ModalHeader>{title}</ModalHeader>
      <ModalContent>
        {!user && !error && <LoadingRing />}
        {user && (
          <>
            <p>
              <Translate name="portfolio.tfaSetupModal.contentUpper" />
            </p>

            <p>
              <Translate name="portfolio.tfaSetupModal.contentLower" args={[<b key={0}>{user.email}</b>]} />
            </p>
            <Button variant="link" onClick={onResendEmail}>
              <Translate name="portfolio.tfaSetupModal.resendEmailButton" />
            </Button>
            <Hint variant="warning">
              <Translate name="portfolio.tfaSetupModal.hint" />
            </Hint>
          </>
        )}
        {error && <Hint variant="danger">{remainingError}</Hint>}
      </ModalContent>
      <ModalFooter>
        <ModalFooterButtons
          actionButtons={[
            {
              name: 'cancel',
              size: 'large',
              content: <Translate name="common.cancel" />,
              onClick: onClose,
            },
          ]}
        />
      </ModalFooter>
    </Modal>
  );
};

export default TfaDeviceSetupModal;
